import { Component, Input, OnInit, ViewChild, ElementRef } from '@angular/core';
import { IonNav } from '@ionic/angular';
import { CaseService } from 'src/app/services/case.service';
import { CommonService } from 'src/app/services/common.service';

interface RepayApiResponse {
  status: number;
  message: string;
  result: string;
}

@Component({
  selector: 'app-repay-digital-wallet',
  templateUrl: './repay-digital-wallet.page.html',
  styleUrls: ['./repay-digital-wallet.page.scss'],
})
export class RepayDigitalWalletPage implements OnInit {
  @Input() caseId;
  @Input() responseData;
  @Input() debtorDetails;
  @Input() paymentMethod: string;
  @Input() amount: number;
  @ViewChild('iframeContainer', { static: false }) iframeContainer: ElementRef;

  title = 'Digital Wallet Payment';
  saveBtnDisabled = false;
  isConfigured = false;
  checkoutFormId: string;
  paymentUrl: string;
  paymentIframe: HTMLIFrameElement;
  private messageHandler: (event: MessageEvent) => void;

  constructor(
    private caseService: CaseService,
    private commonService: CommonService,
    private ionNav: IonNav
  ) {
    this.messageHandler = async (event: MessageEvent) => {
      if (this.validateOrigin(event.origin)) {

        if (event.data === 'PAYMENT_SUCCESS' ||
            event.data === 'PAYMENT_ERROR') {

          try {
            await this.makeDigitalWalletPayment();
          } catch (error) {
            this.commonService.showToast('Error processing payment', 'danger');
            this.dismiss();
          }
        }
      } else {
        console.log('Origin validation failed for:', event.origin);
      }
    };
  }

  async makeDigitalWalletPayment() {
    try {
      this.commonService.showLoader('Processing payment...');

      const paymentData = {
        payment_method: this.paymentMethod,
        amount: this.amount,
        source: 'Lateral Repay Digital Wallet',
        customer_id: this.debtorDetails?.debtor_id,
      };

      const response = await this.caseService.makeDigitalWalletPayment(this.caseId, paymentData).toPromise() as RepayApiResponse;
      this.commonService.dismissLoader();

      if (response?.status === 200) {
        this.commonService.dismissLoader();
        this.commonService.showToast(response?.message || 'Payment completed successfully!', 'success');
        this.caseService.saveCaseDetails(localStorage.getItem('debtor_id'));
        this.ionNav.popToRoot();
      }
      else {
        this.commonService.dismissLoader();
        this.commonService.showToast(response?.message || 'Payment failed', 'danger');
        this.dismiss();
      }
    }
    catch (error) {
      this.commonService.dismissLoader();
      this.commonService.showToast('Error processing payment', 'danger');
      this.dismiss();
    }
  }

  validateOrigin(origin: string) {
    const validOrigins = [
        'https://lateral.sandbox.repay.io',
        'https://lateral.repay.io',
        'https://sandbox.repay.io',
        'https://repay.io',
        'https://www.repay.io'
    ];

    const isValid = validOrigins.includes(origin);

    return isValid;
  }

  ngOnInit() {
    this.saveBtnDisabled = false;
    let portalSettings = JSON.parse(localStorage.getItem('portal_settings'));

    if (portalSettings?.payment_type?.repay?.label) {
      this.title = portalSettings.payment_type.repay.label;
    }

    this.isConfigured = true;
    this.initiatePayment();

    window.addEventListener('message', this.messageHandler);
  }

  async initiatePayment() {
    this.saveBtnDisabled = true;
    this.commonService.showLoader('Initializing payment...');

    try {
      const checkoutFormData = {
        payment_method: this.paymentMethod,
        source: 'Lateral API',
      };

      const checkoutResponse = await this.caseService.getRepayCheckoutForm(checkoutFormData).toPromise() as RepayApiResponse;

      if (checkoutResponse?.result !== 'false' && checkoutResponse?.result) {
        this.checkoutFormId = checkoutResponse.result;
        this.commonService.showToast(checkoutResponse.message, 'success');

        const paymentData = {
          amount: this.amount,
          payment_method: this.paymentMethod,
          customer_id: this.debtorDetails?.debtor_id,
          transaction_type: 'sale',
          channel_user: this.debtorDetails?.emails[0].account,
          payment_channel: 'web',
          source: 'Lateral API',
          checkout_form_id: this.checkoutFormId,
        };

        const urlResponse = await this.caseService.getRepayDigitalWalletUrl(paymentData).toPromise() as RepayApiResponse;

        if (urlResponse?.result !== 'false' && urlResponse?.result) {
          this.paymentUrl = urlResponse.result;

          setTimeout(() => {
            this.loadIframe();
          }, 100);
        }
        else {
          this.commonService.showToast(urlResponse?.message || 'Failed to get payment URL', 'danger');
          this.dismiss();
        }
      }
      else {
        this.commonService.showToast(checkoutResponse?.message || 'Failed to initialize payment', 'danger');
        this.dismiss();
      }
    }
    catch (error) {
      this.commonService.showToast('Error initializing payment', 'danger');
      this.dismiss();
    }
    finally {
      this.saveBtnDisabled = false;
      this.commonService.dismissLoader();
    }
  }

  private loadIframe() {
    if (this.iframeContainer && this.paymentUrl) {
      if (this.paymentIframe && this.paymentIframe.parentNode) {
        this.paymentIframe.parentNode.removeChild(this.paymentIframe);
      }

      this.paymentIframe = document.createElement('iframe');
      this.paymentIframe.src = this.paymentUrl;
      this.paymentIframe.style.width = '100%';
      this.paymentIframe.style.height = '600px';
      this.paymentIframe.style.border = 'none';
      this.paymentIframe.style.borderRadius = '8px';
      this.paymentIframe.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';

      this.paymentIframe.onerror = () => {
        this.commonService.showToast('Failed to load payment form', 'danger');
      };

      this.iframeContainer.nativeElement.appendChild(this.paymentIframe);
    }
  }

  dismiss() {
    this.ionNav.pop();
  }
}